// includes/js-src/janus-engine/pl/ability-cost.ts — calculate_single_attack_ability_cost() twin (calc_pl_system.php:1428–2688).
// The CONTENT price of one ability / spell row: damage roll, status applications, misc/AP/light/force grants, the
// cross-referenced families (charge grants, summons, kill-transform, shapeshift, unlocks — STORED PL of the target, never
// a recursive compile), roll / skill / defense grants, the self-row granted-categories fold, then the self-drawback cap.
// Plan §6, §14 amendments 4/7. Every branch is ported in PHP order; notes are reproduced byte for byte (the breakdown is
// the per-pass oracle).
import type { ItemFieldRow } from '../compile/item-field-reader';
import { readItemNumericSlot } from '../compile/enchantment-template';
import { readItemUuidSlot } from '../compile/inventory-raw-readers';
import { phpJsonTruthy, phpJsonInt } from '../compile/php-json-values';
import { isPhpNumeric, phpJsonFloat } from '../compile/php-decimal-values';
import { phpRound, phpInt, phpIntRound } from './php-round';
import { damageCostGpDetailed } from './cost';
import { plCatalogs, statusQuantityInfo, checkUuid } from './contract';
import { plCostLayerEntryUuid } from './layer-cells';
import type { Row } from './read-context';
import { isObj } from './read-context';
import { PlRefusal, readXrefTarget, plPricingStack } from './xref';
import { type PassEnv, lv, le, lh, itemField, itemType, miscFieldList, miscFieldGp, miscPerEntryRows, calculateEssencesAndSenses, calculateSpeeds, calculateStatusEffectModifiers, calculateSkillTiers, newAccumulator } from './passes';

export interface AbilityCostResult { gp: number; components: Row }

/** ft_pl_duration_label() */
// A SUMMON CYCLE (summon, controlled kill-transform, shapeshift — every creature-PL read an ability makes): a creature carries — directly or
// through anything it holds — an ability that calls more of ITSELF (Time Elemental's Recall, Spawn Gulthir, a chorus whose dirge turns the
// slain into more chorus). Its price includes the ability, and the ability's read its stored PL, so neither could ever be stored. Owner
// 2026-10-10: break the cycle — while a creature is being priced (calc-pl.ts plPricingStack), a call of THAT creature contributes nothing.
// The creature's stored PL is then its price without the self-summon, and the ability priced on its own reads that stored number: no
// value feeds back into itself, so nothing inflates on re-price, and the rule follows whatever grant path carries the ability.
// A MUTUAL cycle (C calls D, D calls C — owner 2026-10-10): both stored values would feed into each other on every re-price. Broken by a
// fixed order: when the summoned creature D calls back a creature being priced, the member with the SMALLER id skips its call to the other.
// So C (smaller) stores its price without D, and D stores its price reading that — whichever page heals first, the numbers are the same.
const CREATURE_CALL_LAYERS = ['summon', 'transform_killed_target_into_controlled_creature', 'shapeshift',
    'user_choice_1_shapeshift', 'user_choice_2_shapeshift', 'user_choice_3_shapeshift', 'user_choice_4_shapeshift'];
/** The creatures a definition calls — its own layers and every catalog entry it carries (its abilities' nested shells). */
function creatureCallsOf(def: Row): Set<string> {
    const out = new Set<string>();
    const scan = (node: unknown): void => {
        const layers = isObj(node) && isObj(node.item_layer_types) ? node.item_layer_types as Row : null;
        if (!layers) return;
        for (const name of CREATURE_CALL_LAYERS) for (const e of Array.isArray(layers[name]) ? layers[name] as unknown[] : []) { const id = plCostLayerEntryUuid(e).toUpperCase(); if (id) out.add(id); }
    };
    scan(def);
    if (isObj(def.catalog)) for (const entry of Object.values(def.catalog as Row)) scan(entry);
    return out;
}
function readSummonTarget(env: PassEnv, summonId: string): { pl: number; name: string } | null {
    const id = summonId.toUpperCase();
    const stack = plPricingStack(env.ctx);
    if (stack.includes(id)) return null;
    // the CALLER is the creature being priced right now (the top of the stack); only a verified reverse edge D → caller is a mutual pair
    // (a longer chain A → B → C → A is not one, and keeps its edges — Codex review 2026-10-10)
    const caller = stack.length ? stack[stack.length - 1]! : '';
    if (caller !== '' && caller < id) {
        const def = env.ctx.definition(id);
        if (def && creatureCallsOf(def).has(caller)) return null;
    }
    return readXrefTarget(env.ctx, summonId);
}

export function durationLabel(env: PassEnv, arr: Row): string {
    const oneTurn: Record<string, string> = {
        effect_ends_on_target_start_of_turn: "until start of target's turn", effect_ends_on_target_end_of_turn: "until end of target's turn",
        effect_ends_on_start_of_caster_next_turn: "until start of caster's next turn", effect_ends_on_end_of_caster_next_turn: "until end of caster's next turn",
    };
    for (const [flag, label] of Object.entries(oneTurn)) if (phpJsonTruthy(lv(env, arr, flag))) return label;
    const int = phpJsonInt(lv(env, arr, 'duration_int') ?? 0);
    const typeRaw = lv(env, arr, 'duration_type') ?? '';
    const type = typeof typeRaw === 'string' ? typeRaw : String(typeRaw);
    if (int === 0 && type === '') return 'permanent';
    if (type === 'permanent' || type === '') return 'permanent';
    return int + ' ' + (int === 1 ? type.replace(/s+$/, '') : type);
}
/** ft_pl_target_debuff_abs(&$bd): flips negative priced rows positive (tagging them) IN PLACE and returns the recomputed
 * sum — the PHP by-ref semantics. Module-private: only ever applied to rows this pricer built itself, never to inputs. */
function targetDebuffAbs(bd: Row | unknown[]): number {
    let sum = 0.0;
    const values = Array.isArray(bd) ? bd : Object.values(bd);
    for (const v of values) {
        if (!isObj(v) && !Array.isArray(v)) continue;
        const row = v as Row;
        if (Object.prototype.hasOwnProperty.call(row, 'gp') && isPhpNumeric(row.gp)) {
            if (phpJsonFloat(row.gp) < 0) {
                row.gp = Math.abs(phpJsonFloat(row.gp));
                if (row.pl !== undefined && isPhpNumeric(row.pl)) row.pl = Math.abs(phpJsonFloat(row.pl));
                row.target_debuff = true;
            }
            sum += phpJsonFloat(row.gp);
            continue;
        }
        sum += targetDebuffAbs(row);
    }
    return sum;
}
/** PHP float → string in a concatenation (shortest round-trip; integral floats print without a fraction). */
const fstr = (v: number): string => String(v);
const orbCount = (env: PassEnv, arr: Row, layer: string): number => le(env, arr, layer).filter(e => (typeof e === 'string' ? e : (isObj(e) ? (e.value ?? '') : '')) !== '').length;
const clampOrb = (env: PassEnv, v: number, min: number): number => Math.min(env.contract.constants.channelOrbQtyMax, Math.max(min, v));

export function calculateSingleAttackAbilityCost(env: PassEnv, abilityArr: Row, parentItemArr: Row): AbilityCostResult {
    const cats = plCatalogs(env.contract);
    const costs = env.costs;
    const read = (name: string): unknown => lv(env, abilityArr, name);
    const has = (name: string): boolean => lh(env, abilityArr, name);
    const entries = (name: string): unknown[] => le(env, abilityArr, name);
    const components: Row = {
        damage_roll: 0, status_applications: [] as Row[], force_movement: 0, skill_roll_modifiers: 0, roll_debuffs: 0, aoe: 0,
        ap_modifiers: [] as Row[], light_modifiers: [] as Row[], misc_modifiers: Object.create(null) as Row, roll_modifier_grants: [] as Row[], damage_defense_grants: [] as Row[],
    };
    const misc = components.misc_modifiers as Row;
    const statusApps = components.status_applications as Row[];
    const rollGrants = components.roll_modifier_grants as Row[];
    const dmgDefGrants = components.damage_defense_grants as Row[];

    // TARGET-DIRECTION
    const abilityOwnType = itemType(env, abilityArr);
    let hasTargetSideStatus = false;
    for (const tl of ['status_effect_applied_to_target_after_cast', 'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_removed_from_target_after_cast', 'status_effect_removed_from_target_on_hit', 'status_effect_removed_from_target_after_contesting_roll_failed']) {
        if (has(tl)) { hasTargetSideStatus = true; break; }
    }
    const directionRangeId = readItemUuidSlot(read('range') ?? '');
    const directionRangeVar = directionRangeId !== '' ? (cats.rangeTypes.getVariableName(directionRangeId) ?? 'self') : 'self';
    const targetsOthers = ['attack_ability', 'opportunity_attack_ability'].includes(abilityOwnType) || hasTargetSideStatus
        || (directionRangeVar !== 'self' && (phpJsonTruthy(read('dice')) || read('roll_type') !== null));

    // DAMAGE ROLL
    if ((phpJsonTruthy(read('dice')) && phpJsonTruthy(read('die'))) || phpInt(readItemNumericSlot(read('mod') ?? 0, 0)) !== 0) {
        const dmg = damageCostGpDetailed(abilityArr as ItemFieldRow, false, 1, false, (r, n) => env.ctx.layerValue(r, n), env.contract);
        const rtUuid = readItemUuidSlot(read('roll_type') ?? '');
        const rtVar = rtUuid !== '' ? (cats.rollTypes.getVariableName(rtUuid) ?? '') : '';
        if (rtVar === 'healing_roll' || rtVar === 'extra_healing_roll') {
            const pushPart = phpInt(Number(dmg.details.push_gp ?? 0));
            dmg.gp = pushPart + phpIntRound((dmg.gp - pushPart) * 0.50);
            dmg.details.healing_rate = 0.50;
        }
        components.damage_roll = dmg.gp;
        components.damage_roll_details = dmg.details;
    }

    // CHAIN / LEAP (chain_targets_max + chain_dice / chain_die, 2026-10-07 — ability/rolls/chain-leap.ts): on a landed contest up to N
    // more creatures each take the chain dice of the host's type. Priced as N extra damage rolls at 50% (it needs the hit). TUNABLE.
    // DELAYED DETONATION splash (damage_on_status_expiry_splash_*, ability/statuses/status-expiry-detonation.ts): the splash dice on the
    // creatures near the holder when the status runs out, priced as ONE extra damage roll at 75% (the mark can be removed first). TUNABLE.
    const extraDiceGp = (dice: number, die: number): number => damageCostGpDetailed(abilityArr as ItemFieldRow, false, 1, false,
        (r, n) => n === 'dice' ? dice : n === 'die' ? die : (n === 'mod' || n === 'push_ft_after_hit') ? null : env.ctx.layerValue(r, n), env.contract).gp;
    const chainMax = phpJsonInt(read('chain_targets_max') ?? 0), chainDice = phpJsonInt(read('chain_dice') ?? 0), chainDie = phpJsonInt(read('chain_die') ?? 0);
    if (chainMax > 0 && chainDice > 0 && chainDie > 0) {
        misc.chain_leap = { value: chainMax, gp: phpIntRound(extraDiceGp(chainDice, chainDie) * chainMax * 0.5),
            note: `Leaps to up to ${chainMax} more creature(s) for ${chainDice}d${chainDie} each on a hit (50% — needs the hit)` };
    }
    const splashDice = phpJsonInt(read('damage_on_status_expiry_splash_dice') ?? 0), splashDie = phpJsonInt(read('damage_on_status_expiry_splash_die') ?? 0);
    if (phpJsonTruthy(read('damage_on_status_expiry')) && splashDice > 0 && splashDie > 0 && phpJsonInt(read('damage_on_status_expiry_splash_ft') ?? 0) > 0) {
        misc.damage_on_status_expiry_splash = { value: phpJsonInt(read('damage_on_status_expiry_splash_ft') ?? 0), gp: phpIntRound(extraDiceGp(splashDice, splashDie) * 0.75),
            note: `Detonation splashes ${splashDice}d${splashDie} on creatures near the holder (75%)` };
    }

    // STATUS APPLICATIONS + REMOVALS
    const statusLayers = ['status_effect_applied_to_target_after_cast', 'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_target_after_contesting_roll_failed',
        'status_effect_applied_to_caster_on_hit', 'status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_caster_after_contesting_roll_failed', 'status_effect_applied_to_caster_when_contestee_wins',
        'status_effect_removed_from_target_after_cast', 'status_effect_removed_from_target_on_hit', 'status_effect_removed_from_target_after_contesting_roll_failed',
        'status_effect_removed_from_caster_after_cast', 'status_effect_removed_from_caster_on_hit', 'status_effect_removed_from_caster_after_contesting_roll_failed'];
    const statusDurationMult = costs.getDurationMultiplier(abilityArr as ItemFieldRow, (r, n) => env.ctx.layerValue(r, n));
    const statusDurationLabel = durationLabel(env, abilityArr);
    for (const layer of statusLayers) {
        const list = entries(layer);
        if (list.length === 0) continue;
        const isCasterSelf = layer.includes('_caster_');
        const isRemoval = layer.includes('_removed_from_');
        for (const entry of list) {
            const seUuid = plCostLayerEntryUuid(entry);
            if (seUuid === '') continue;
            const varName = cats.statusEffects.getVariableName(seUuid);
            const statusData = cats.statusEffects.getById(seUuid);
            const statusName = statusData?.item_name ?? varName;
            if (!varName && checkUuid(seUuid)) {
                if (isRemoval) continue;
                const applied = readXrefTarget(env.ctx, seUuid);
                const appliedPlGp = applied.pl * 100;
                if (appliedPlGp <= 0) continue;
                const appliedReliability = layer.includes('_after_contesting_roll_failed') ? 0.55 : layer.includes('_when_contestee_wins') ? 0.45 : 1.00;
                const gp = phpIntRound(appliedPlGp * 0.40 * appliedReliability * (isCasterSelf ? 0.75 : 1.0) * statusDurationMult);
                if (gp === 0) continue;
                const derivation: Row = { applied_item_pl_gp: phpIntRound(appliedPlGp), application_rate: 0.40, reliability: appliedReliability };
                if (isCasterSelf) derivation.self_discount = 0.75;
                if (statusDurationMult != 1.0) { derivation.duration_multiplier = statusDurationMult; derivation.duration_label = statusDurationLabel; }
                const nm = env.ctx.itemField(applied.def as ItemFieldRow, 'item_name');
                statusApps.push({ name: (nm === null || nm === undefined) ? 'Applied item' : nm, var: '', layer, gp, derivation });
                continue;
            }
            const statusCost = costs.getStatusApplicationCostDetailed(layer, varName, isCasterSelf);
            let gp = statusCost.gp;
            const details: Row = { ...statusCost.details };
            const statusType = statusData?.status_type ?? '';
            const selfDrawback = isCasterSelf && ((!isRemoval && statusType === 'bane') || (isRemoval && statusType === 'boon'));
            if (selfDrawback) gp = -gp;
            if (!isRemoval && gp !== 0 && statusDurationMult != 1.0) { gp = phpIntRound(gp * statusDurationMult); details.duration_multiplier = statusDurationMult; details.duration_label = statusDurationLabel; }
            if (!isRemoval && gp !== 0) {
                const qInfo = statusQuantityInfo(seUuid, env.contract);
                if (qInfo !== null) {
                    let qStacks = phpJsonInt(read('stacks_applied') ?? 0);
                    if (qStacks < 1) qStacks = qInfo.stacks;
                    qStacks = Math.max(1, Math.min(qInfo.max, qStacks));
                    if (qStacks > 1) { gp = phpIntRound(gp * qStacks); details.stack_multiplier = qStacks; }
                }
            }
            if (gp !== 0) statusApps.push({ name: statusName, var: varName, layer, gp, derivation: details });
        }
    }
    if (phpJsonTruthy(read('random_disease_applied_to_target_after_contesting_roll_failed'))) {
        const rdLayer = 'random_disease_applied_to_target_after_contesting_roll_failed';
        const rdCost = costs.getStatusApplicationCostDetailed(rdLayer, 'diseased', false);
        let rdGp = 2 * phpInt(rdCost.gp);
        const details: Row = { ...rdCost.details };
        if (rdGp !== 0 && statusDurationMult != 1.0) { rdGp = phpIntRound(rdGp * statusDurationMult); details.duration_multiplier = statusDurationMult; details.duration_label = statusDurationLabel; }
        if (rdGp !== 0) statusApps.push({ name: 'Random Disease', var: 'diseased', layer: rdLayer, gp: rdGp, derivation: { ...details, random_disease_multiplier: 2 } });
    }

    // MISC — the full tables
    const miscExcluded = ['ac_mod', 'ac_mod_magical', 'ac_mod_non_magical', 'damage_mitigation', 'extra_attack', 'reach'];
    for (const field of miscFieldList(env)) {
        if (miscExcluded.includes(field)) continue;
        let value = phpJsonInt(read(field) ?? 0);
        if (value === 0) continue;
        if (field === 'can_breath_underwater') value = 1;
        const gp = miscFieldGp(env, field, value);
        if (gp === 0) continue;
        const row: Row = { value, gp };
        if (targetsOthers && gp < 0 && field !== 'attack_automatically_misses') { row.gp = Math.abs(gp); row.target_debuff = true; row.note = 'Debuff landed on the target — priced as control power'; }
        misc[field] = row;
    }
    for (const [field, row] of Object.entries(miscPerEntryRows(env, abilityArr))) misc[field] = { ...row };

    // extra_attack
    if (phpJsonTruthy(read('extra_attack'))) { const value = phpJsonInt(read('extra_attack')); misc.extra_attack = { value, gp: value * 1500 }; }

    // AP-ECONOMY GRANTS
    for (const field of ['ap_change_at_start_of_turn', 'ap_change_during_turn', 'weapon_attack_count_change', 'ap_change_to_caster_during_turn', 'reaction_reset']) {
        const value = phpJsonInt(read(field) ?? 0);
        if (value === 0) continue;
        const gp = phpIntRound(value * costs.getApModifierCost(field) * statusDurationMult);
        if (gp === 0) continue;
        const row: Row = { value, gp, note: 'AP economy grant' + (statusDurationMult != 1.0 ? ' × ' + fstr(statusDurationMult) + ' duration (' + statusDurationLabel + ')' : '') };
        if (targetsOthers && gp < 0 && field !== 'ap_change_to_caster_during_turn') { row.gp = Math.abs(gp); row.target_debuff = true; row.note = 'AP drain landed on the target — priced as control power'; }
        misc[field] = row;
    }

    // MACHINIST ORBS
    let channelOrbCount = orbCount(env, abilityArr, 'channel_orb');
    channelOrbCount *= clampOrb(env, phpJsonInt(read('channel_orb_qty') ?? 1), 1); // ft_channel_orb_qty
    if (channelOrbCount > 0) misc.channel_orb = { value: channelOrbCount, gp: channelOrbCount * 300, note: 'Orbs channeled (placeholder rate)' };
    const channelRandomOrbCount = clampOrb(env, phpJsonInt(read('channel_random_orb') ?? 0), 0);
    if (channelRandomOrbCount > 0) misc.channel_random_orb = { value: channelRandomOrbCount, gp: channelRandomOrbCount * 300, note: 'Random orbs channeled (placeholder rate)' };
    const channelChosenOrbCount = clampOrb(env, phpJsonInt(read('channel_chosen_orb') ?? 0), 0);
    if (channelChosenOrbCount > 0) misc.channel_chosen_orb = { value: channelChosenOrbCount, gp: channelChosenOrbCount * 400, note: 'Orbs channeled, player picks the types at cast (placeholder rate)' };
    if (phpJsonInt(read('channel_orb_each_turn') ?? 0) === 1) {
        const eachTurnBaseGp = Number((misc.channel_orb as Row | undefined)?.gp ?? 0) + Number((misc.channel_random_orb as Row | undefined)?.gp ?? 0) + (channelChosenOrbCount * 300);
        if (eachTurnBaseGp > 0) {
            const repeats = env.contract.constants.orbQueueDefaultSlots;
            const gp = phpIntRound(eachTurnBaseGp * repeats * statusDurationMult);
            misc.channel_orb_each_turn = { value: repeats, gp, note: 'Channel block repeats each turn for the duration, priced at ' + repeats + ' expected extra turns × ' + fstr(statusDurationMult) + ' duration (' + statusDurationLabel + ') (placeholder rate)' };
        }
    }
    const consumeOrbCount = orbCount(env, abilityArr, 'consume_orb');
    if (consumeOrbCount > 0) misc.consume_orb = { value: consumeOrbCount, gp: -150 * consumeOrbCount, note: 'Channeled orbs consumed without evoking (placeholder rate)' };
    const evokeOrbCount = phpJsonInt(read('evoke_orb') ?? 0);
    if (evokeOrbCount > 0) misc.evoke_orb = { value: evokeOrbCount, gp: evokeOrbCount * 150, note: 'Orbs evoked (placeholder rate)' };
    const evokeOrbRepeat = phpJsonInt(read('evoke_orb_repeat') ?? 0);
    if (evokeOrbRepeat > 0) misc.evoke_orb_repeat = { value: evokeOrbRepeat, gp: evokeOrbRepeat * 150, note: 'Front orb evoked repeatedly (placeholder rate)' };
    if (phpJsonInt(read('evoke_all_orbs') ?? 0) === 1) { const n = env.contract.constants.orbQueueDefaultSlots; misc.evoke_all_orbs = { value: n, gp: n * 150, note: 'Entire orb queue evoked, priced at baseline slots (placeholder rate)' }; }
    const removeRandomBane = phpJsonInt(read('remove_random_bane') ?? 0);
    if (removeRandomBane > 0) misc.remove_random_bane = { value: removeRandomBane, gp: removeRandomBane * 200, note: 'Random bane cleanse (placeholder rate)' };
    if (phpJsonTruthy(read('target_all_enemies')) && phpJsonTruthy(read('die'))) misc.target_all_enemies = { value: 1, gp: 300, note: 'Strikes every enemy (placeholder rate)' };
    const targetRandomEnemyQty = phpJsonInt(read('target_random_enemy_qty') ?? 0);
    if (has('target_random_enemy') && !has('target_all_enemies') && targetRandomEnemyQty > 1) {
        const extra = targetRandomEnemyQty - 1;
        misc.target_random_enemy_qty = { value: targetRandomEnemyQty, gp: Math.min(extra * 150, 300), note: 'Extra random targets (placeholder rate)' };
    }

    // LIGHT GRANTS
    let lightMultiplier = 1.0;
    if (phpJsonTruthy(read('magical_light'))) lightMultiplier *= costs.getLightModifierCost('magical_light');
    if (phpJsonTruthy(read('magical_darkness'))) lightMultiplier *= costs.getLightModifierCost('magical_darkness');
    for (const field of ['bright_light_dim_light', 'limited_visibility_ft']) {
        const ft = phpJsonInt(read(field) ?? 0);
        if (ft === 0) continue;
        const gp = phpIntRound(costs.getLightModifierCost(field, ft) * lightMultiplier * statusDurationMult);
        if (gp === 0) continue;
        misc[field] = { value: ft, gp, note: 'Light grant' };
    }

    // FORCE MOVEMENT
    const pushPricedInDamage = isObj(components.damage_roll_details) && Object.prototype.hasOwnProperty.call(components.damage_roll_details, 'push_gp');
    let forceGp = 0;
    for (const field of ['pull_ft_after_hit', 'push_ft_after_hit']) {
        if (field === 'push_ft_after_hit' && pushPricedInDamage) continue;
        const ft = phpJsonInt(read(field) ?? 0);
        if (ft === 0) continue;
        forceGp += phpIntRound(costs.getForceMovementModifierCost(field, ft));
    }
    for (const field of ['push_any_direction', 'pull_to_caster_center']) {
        if (!phpJsonTruthy(read(field))) continue;
        forceGp += phpIntRound(costs.getForceMovementModifierCost(field));
    }
    if (forceGp !== 0) components.force_movement = (components.force_movement as number) + forceGp;

    // IMMUNE-TO-ABILITY GRANTS
    const immuneAbilityEntries = entries('immune_to_ability');
    if (immuneAbilityEntries.length > 0) {
        const n = immuneAbilityEntries.length;
        const gp = phpIntRound(n * 750 * statusDurationMult);
        if (gp !== 0) misc.immune_to_ability = { value: n, gp, note: 'Immunity to ' + n + ' specific abilit' + (n === 1 ? 'y' : 'ies') + ' (750 gp each)' };
    }

    // CHARGE-ECONOMY GRANTS
    const chargeGrantSpecs = [
        { ability_layer: 'add_charges_to_ability', qty_layer: 'add_charges_qty', factor: 0.25, label: 'Charge generation' },
        { ability_layer: 'add_charges_to_ability_for_target', qty_layer: 'add_charges_to_ability_qty_for_target', factor: 0.25, label: 'Charge generation (granted to target)' },
        { ability_layer: 'increase_max_charges_of_ability', qty_layer: 'increase_max_charges_by_qty', factor: 0.125, label: 'Charge capacity' },
    ];
    for (const spec of chargeGrantSpecs) {
        const grantEntries = entries(spec.ability_layer);
        if (grantEntries.length === 0) continue;
        const qty = Math.max(1, phpInt(readItemNumericSlot(read(spec.qty_layer) ?? 1, 1)));
        const qtyEff = 1.0 + (qty >= 2 ? 0.5 : 0) + (qty >= 3 ? 0.25 : 0) + 0.1 * Math.max(0, qty - 3);
        for (const grantEntry of grantEntries) {
            const targetId = plCostLayerEntryUuid(grantEntry);
            if (targetId === '') continue;
            const target = readXrefTarget(env.ctx, targetId);
            const targetGp = target.pl * 100;
            const gp = phpIntRound(Math.min(Math.max(250, targetGp * spec.factor * qtyEff), Math.max(250, targetGp)));
            misc['charge_grant_' + spec.ability_layer + '_' + targetId] = {
                value: qty, gp,
                note: spec.label + ': ' + qty + ' charge' + (qty === 1 ? '' : 's') + ' of ' + target.name + ' (ability PL ' + fstr(phpRound(target.pl, 1)) + ' × ' + fstr(spec.factor) + ' × ' + fstr(phpRound(qtyEff, 2)) + ' qty, min 250 gp)',
            };
        }
    }

    // SUMMONS
    const summonEntries = entries('summon');
    const summonTemplateId = read('summon_monster_template');
    let summonTemplateMult = 1.0;
    if (summonEntries.length > 0 && typeof summonTemplateId === 'string' && checkUuid(summonTemplateId)) {
        const templateName = env.ctx.monsterTemplateName(abilityArr, summonTemplateId);
        if (templateName === null) throw new PlRefusal('pl-summon-template');
        summonTemplateMult = 1.5;
        const swarm = /Swarm\s*:\s*(\d+)/i.exec(templateName);
        if (swarm) {
            const n = Math.max(2, phpInt(Number(swarm[1] ?? 0)));
            summonTemplateMult = Math.min(4.0, 1.0 + 0.5 * Math.log2(n));
        }
    }
    for (const summonEntry of summonEntries) {
        const summonId = plCostLayerEntryUuid(summonEntry);
        if (summonId === '') continue;
        const summon = readSummonTarget(env, summonId);
        if (summon === null) continue;
        const summonGp = Math.max(500, phpIntRound(summon.pl * 100 * 0.40 * statusDurationMult * summonTemplateMult));
        misc['summon_' + summonId] = {
            value: 1, gp: summonGp,
            note: 'Summon: ' + summon.name + ' (creature PL ' + fstr(phpRound(summon.pl, 1)) + ' × 0.40'
                + (summonTemplateMult != 1.0 ? ' × ' + fstr(phpRound(summonTemplateMult, 2)) + ' horde' : '')
                + (statusDurationMult != 1.0 ? ' × ' + fstr(statusDurationMult) + ' duration' : '') + ', min 500 gp)',
        };
    }
    if (phpJsonTruthy(read('summon_transforms_targeted_corpse')) && summonEntries.length > 0) {
        misc.summon_transforms_targeted_corpse = { value: 1, gp: 250, note: 'Corpse-raiser: the summon rises as the targeted corpse, inheriting and re-equipping its gear' };
    }

    // CONTROLLED KILL-TRANSFORM
    const killTransformEntry = read('transform_killed_target_into_controlled_creature');
    if (killTransformEntry !== null) {
        const ktId = plCostLayerEntryUuid(killTransformEntry);
        if (ktId !== '' && checkUuid(ktId)) {
            const kt = readSummonTarget(env, ktId);
            // null: a kill-transform into the creature being priced inline (summon cycle) — skipped
            if (kt !== null) {
                const ktGp = Math.max(500, phpIntRound(kt.pl * 100 * 0.40));
                misc['controlled_kill_transform_' + ktId] = { value: 1, gp: ktGp, note: 'Controlled kill-transform: ' + kt.name + ' (creature PL ' + fstr(phpRound(kt.pl, 1)) + ' × 0.40, min 500 gp)' };
            }
        }
    }

    // SHAPESHIFT
    const shapeshiftFraction = 0.50;
    for (const layer of ['shapeshift', 'user_choice_1_shapeshift', 'user_choice_2_shapeshift', 'user_choice_3_shapeshift', 'user_choice_4_shapeshift']) {
        for (const entry of entries(layer)) {
            const formId = plCostLayerEntryUuid(entry);
            if (formId === '') continue;
            const form = readSummonTarget(env, formId);
            if (form === null) continue; // a shapeshift into a creature priced inline (summon cycle)
            const gp = Math.max(500, phpIntRound(form.pl * 100 * shapeshiftFraction * statusDurationMult));
            misc['shapeshift_' + formId] = { value: 1, gp, note: 'Shapeshift: ' + form.name + ' (creature PL ' + fstr(phpRound(form.pl, 1)) + ' × ' + fstr(shapeshiftFraction) + (statusDurationMult != 1.0 ? ' × ' + fstr(statusDurationMult) + ' duration' : '') + ', min 500 gp)' };
        }
    }

    // CASTER MANA / SANGUINE RIDERS
    if (phpJsonTruthy(read('change_caster_mana'))) { const value = phpJsonInt(read('change_caster_mana')); misc.change_caster_mana = { value, gp: value * 150 }; }
    if (phpJsonTruthy(read('ritual_spell_mana_discount'))) { const value = phpJsonInt(read('ritual_spell_mana_discount')); misc.ritual_spell_mana_discount = { value, gp: phpIntRound(value * 150 * 0.6) }; }
    if (phpJsonTruthy(read('arcane_fletcher'))) misc.arcane_fletcher = { value: 1, gp: 750 };
    if (phpJsonTruthy(read('next_spell_mana_discount'))) { const value = Math.max(0, Math.min(10, phpJsonInt(read('next_spell_mana_discount')))); misc.next_spell_mana_discount = { value, gp: phpIntRound(value * 150 * 0.5) }; }
    if (phpJsonTruthy(read('next_spell_range_multiplier'))) { const value = Math.max(0, Math.min(9, phpJsonInt(read('next_spell_range_multiplier')) - 1)); misc.next_spell_range_multiplier = { value: value + 1, gp: phpIntRound(value * 150 * 0.5) }; }
    if (phpJsonTruthy(read('counterspell_against_dis'))) { const value = Math.max(0, Math.min(5, phpJsonInt(read('counterspell_against_dis')))); misc.counterspell_against_dis = { value, gp: phpIntRound(value * 150 * 0.25) }; }
    if (has('next_spell_ap_override')) { const override = Math.max(0, Math.min(3, phpJsonInt(read('next_spell_ap_override')))); const saved = Math.max(0, 2 - override); misc.next_spell_ap_override = { value: override, gp: phpIntRound(saved * 150 * 0.75) }; }
    if (phpJsonTruthy(read('next_spell_upcast_levels'))) { const value = Math.max(0, Math.min(3, phpJsonInt(read('next_spell_upcast_levels')))); misc.next_spell_upcast_levels = { value, gp: phpIntRound(value * 150 * 0.5) }; }
    if (phpJsonTruthy(read('next_spell_target_rider'))) misc.next_spell_target_rider = { value: 1, gp: phpIntRound(150 * 0.75) };
    if (phpJsonTruthy(read('next_spell_target_opportunity_attack'))) misc.next_spell_target_opportunity_attack = { value: 1, gp: phpIntRound(150 * 0.5) };
    if (phpJsonTruthy(read('next_spell_aoe_effect_duplicated'))) misc.next_spell_aoe_effect_duplicated = { value: 1, gp: phpIntRound(150 * 1.5) };
    if (phpJsonTruthy(read('next_spell_paoe_applied_to_targets'))) misc.next_spell_paoe_applied_to_targets = { value: 1, gp: phpIntRound(150 * 0.5) };
    if (phpJsonTruthy(read('next_spell_aoe_changed_by_percentage'))) { const value = Math.max(0, Math.min(300, phpJsonInt(read('next_spell_aoe_changed_by_percentage')))); misc.next_spell_aoe_changed_by_percentage = { value, gp: phpIntRound((value / 50) * 150 * 0.5) }; }
    if (phpJsonTruthy(read('next_spell_damage_dice_changed_by_percentage'))) { const value = Math.max(0, Math.min(300, phpJsonInt(read('next_spell_damage_dice_changed_by_percentage')))); misc.next_spell_damage_dice_changed_by_percentage = { value, gp: phpIntRound((value / 50) * 150 * 0.75) }; }
    if (phpJsonTruthy(read('next_spell_self_range_becomes_reach'))) misc.next_spell_self_range_becomes_reach = { value: 1, gp: phpIntRound(150 * 0.75) };
    if (phpJsonTruthy(read('next_spell_twin_cast'))) misc.next_spell_twin_cast = { value: 1, gp: phpIntRound(150 * 1.0) };
    if (phpJsonTruthy(read('next_spell_bounce_cast'))) misc.next_spell_bounce_cast = { value: 1, gp: phpIntRound(150 * 0.75) };
    if (phpJsonTruthy(read('next_spell_hits_opposing_team_only'))) misc.next_spell_hits_opposing_team_only = { value: 1, gp: phpIntRound(150 * 0.5) };
    if (phpJsonTruthy(read('next_spell_concentration_uncapped'))) misc.next_spell_concentration_uncapped = { value: 1, gp: phpIntRound(150 * 0.5) };
    if (phpJsonTruthy(read('next_spell_damage_type_choice'))) {
        const dtCount = entries('damage_type').length;
        misc.next_spell_damage_type_choice = { value: dtCount > 0 ? dtCount : 'any', gp: phpIntRound(150 * (dtCount > 0 ? 0.25 : 0.5)) };
    }

    // ROLL-TYPE MODIFIER GRANTS
    const grantOneShot = has('triggers_on_next_roll_type') || has('triggers_on_next_roll_type_against');
    const grantUptime = grantOneShot ? 0.10 : 0.40;
    const attackRollCluster = ['spell_melee_attack_roll', 'spell_ranged_attack_roll', 'weapon_melee_attack_roll', 'weapon_ranged_attack_roll'];
    for (const field of ['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis', 'roll_type_is_rolled_at_min', 'roll_type_is_rolled_at_max', 'roll_type_roll_result_is_halved', 'roll_type_roll_result_is_doubled']) {
        let bases: Record<string, number> = {};
        for (const entry of entries(field)) {
            const uuid = plCostLayerEntryUuid(entry);
            if (uuid === '') continue;
            const v = cats.rollTypes.getVariableName(uuid);
            if (!v) continue;
            const base = costs.getRollTypeModifierCost(field, v);
            if (base === 0) continue;
            bases[v] = base;
        }
        if (Object.keys(bases).length === 0) continue;
        if (!field.includes('_against_')) {
            const cluster = Object.fromEntries(Object.entries(bases).filter(([v]) => attackRollCluster.includes(v)));
            if (Object.keys(cluster).length > 1) {
                let strongest = 0;
                for (const b of Object.values(cluster)) if (Math.abs(b) > Math.abs(strongest)) strongest = b;
                const gp = phpIntRound(strongest * 1.25 * grantUptime * statusDurationMult);
                if (gp !== 0) rollGrants.push({ field, roll_type: 'attack_rolls_any', gp, derivation: { base_gp: strongest, cluster_count: Object.keys(cluster).length, cluster_multiplier: 1.25, uptime: grantUptime, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
                bases = Object.fromEntries(Object.entries(bases).filter(([v]) => !(v in cluster)));
            }
        }
        for (const [v, base] of Object.entries(bases)) {
            const gp = phpIntRound(base * grantUptime * statusDurationMult);
            if (gp === 0) continue;
            rollGrants.push({ field, roll_type: v, gp, derivation: { base_gp: base, uptime: grantUptime, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
        }
    }

    // SKILL-ROLL MODIFIER GRANTS
    for (const field of ['skill_roll_adv', 'skill_roll_dis', 'skill_roll_ignore_dis', 'skill_roll_mod_plus1', 'skill_roll_mod_minus1', 'skill_is_rolled_at_min', 'skill_is_rolled_at_max']) {
        for (const entry of entries(field)) {
            const uuid = plCostLayerEntryUuid(entry);
            if (uuid === '') continue;
            const v = cats.skills.getVariableName(uuid) ?? '';
            if (!v) continue;
            const base = costs.getSkillRollModifierCost(field, v);
            if (base === 0) continue;
            const gp = phpIntRound(base * grantUptime * statusDurationMult);
            if (gp === 0) continue;
            rollGrants.push({ field, roll_type: v, gp, derivation: { base_gp: base, uptime: grantUptime, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
        }
    }
    for (const gkey of ['all_skill_rolls_adv', 'all_skill_rolls_dis', 'all_skill_rolls_plus1', 'all_skill_rolls_minus1', 'ascension_skill_point_range_mod_minus1']) {
        if (!phpJsonTruthy(read(gkey))) continue;
        const base = costs.getSkillRollModifierCost(gkey);
        if (base === 0) continue;
        const gp = phpIntRound(base * grantUptime * statusDurationMult);
        if (gp === 0) continue;
        rollGrants.push({ field: gkey, roll_type: '', gp, derivation: { base_gp: base, uptime: grantUptime, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
    }

    // DAMAGE-TYPE DEFENSE GRANTS
    for (const field of ['damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability', 'ignore_damage_type_resistance']) {
        for (const entry of entries(field)) {
            const uuid = plCostLayerEntryUuid(entry);
            if (uuid === '') continue;
            const v = cats.damageTypes.getVariableName(uuid);
            if (!v) continue;
            const base = costs.getDamageModifierCost(field, v);
            if (base === 0) continue;
            const gp = phpIntRound(base * statusDurationMult);
            if (gp === 0) continue;
            dmgDefGrants.push({ field, damage_type: v, gp, derivation: { base_gp: base, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
        }
    }
    for (const gkey of ['spell_damage_resistance', 'spell_damage_vulnerability', 'immune_to_spells', 'immune_to_hostile_spells', 'force_move_resistance', 'immune_to_forced_movement', 'immune_to_disarm', 'ignores_difficult_terrain', 'ignores_snow_and_ice_terrain', 'walks_on_water_and_ice', 'burrows_worked_stone', 'ignores_occupied_space_movement_penalty']) {
        if (!phpJsonTruthy(read(gkey))) continue;
        const base = costs.getDamageModifierCost(gkey);
        if (base === 0) continue;
        dmgDefGrants.push({ field: gkey, damage_type: '', gp: phpIntRound(base * statusDurationMult), derivation: { base_gp: base, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
    }
    for (const skey of env.contract.constants.sizeGatedDefenseLayers) {
        const sval = read(skey);
        if (!isPhpNumeric(sval)) continue;
        const base = sizeGated(env, skey, phpJsonInt(sval));
        if (base === 0) continue;
        dmgDefGrants.push({ field: skey, damage_type: '', value: phpJsonInt(sval), gp: phpIntRound(base * statusDurationMult), derivation: { base_gp: base, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
    }
    for (const vkey of ['all_damage_output_changed_by_percentage', 'all_incoming_damage_changed_by_percentage', 'negate_falling_damage_in_feet']) {
        const val = phpJsonInt(read(vkey) ?? 0);
        if (val === 0) continue;
        const base = costs.getDamageModifierCost(vkey, '', val);
        if (base === 0) continue;
        dmgDefGrants.push({ field: vkey, damage_type: '', value: val, gp: phpIntRound(base * statusDurationMult), derivation: { base_gp: base, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
    }
    for (const akey of ['ac_mod', 'ac_mod_magical', 'ac_mod_non_magical', 'damage_mitigation']) {
        const val = phpJsonInt(read(akey) ?? 0);
        if (val === 0) continue;
        const base = akey === 'damage_mitigation' ? costs.getDamageMitigationCost(val) : costs.getMiscModifierCost(akey, val);
        if (base === 0) continue;
        dmgDefGrants.push({ field: akey, damage_type: '', value: val, gp: phpIntRound(base * statusDurationMult), derivation: { base_gp: base, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
    }

    // TARGET-DEBUFF DIRECTION for the grant families
    if (targetsOthers) { targetDebuffAbs(rollGrants); targetDebuffAbs(dmgDefGrants); }

    // GRANTED CATEGORIES + UNLOCKS + SKILL POINTS — SELF-ROW ONLY
    const selfRowId = itemField(env, abilityArr, 'item_id') ?? '';
    const parentId = itemField(env, parentItemArr, 'item_id') ?? '';
    if (selfRowId !== '' && selfRowId === parentId) {
        const granted = newAccumulator();
        granted.breakdown = {};
        calculateEssencesAndSenses(env, abilityArr, granted, false, true);
        calculateSpeeds(env, abilityArr, granted, false, true);
        calculateStatusEffectModifiers(env, abilityArr, granted, true);
        calculateSkillTiers(env, abilityArr, granted, true);
        let grantedBaseGp = granted.totalGp;
        if (targetsOthers) grantedBaseGp = targetDebuffAbs(granted.breakdown);
        if (phpIntRound(grantedBaseGp) !== 0) {
            const details: Row = {};
            for (const [k, v] of Object.entries(granted.breakdown)) if (isObj(v) && Object.keys(v).length > 0) details[k] = v; // array_filter
            const derivation: Row = { base_gp: phpIntRound(grantedBaseGp), duration_multiplier: statusDurationMult, duration_label: statusDurationLabel };
            if (targetsOthers) derivation.target_debuff_direction = true;
            components.granted_categories = { gp: phpIntRound(grantedBaseGp * statusDurationMult), derivation, details };
        }

        const unlockFractions: Record<string, number> = { spell: 0.25, at_will_spell: 1.00, innate_spell_per_long_rest: 0.60, ability: 1.00, passive_ability: 1.00, quick_ability: 1.00, full_ability: 1.00, free_ability: 1.00, reaction_ability: 1.00, attack_ability: 1.00, opportunity_attack_ability: 1.00, menta: 1.00, curse: 1.00, sanguine: 1.00, feat: 1.00, summon: 0.40 };
        const unlockSpecs: Array<{ layer: string; fraction: number; choice: boolean; label: string }> = [];
        if (has('spell_known')) unlockSpecs.push({ layer: 'spell_known', fraction: 0.25, choice: false, label: 'Spells known' });
        for (const [base, fraction] of Object.entries(unlockFractions)) {
            for (let ci = 1; ci <= 4; ci++) {
                const ul = 'user_choice_' + ci + '_' + base;
                if (has(ul)) unlockSpecs.push({ layer: ul, fraction, choice: true, label: 'Choice of ' + base.replace(/_/g, ' ') });
            }
        }
        for (const spec of unlockSpecs) {
            const optionGps: number[] = [];
            const unresolved = 0; // the browser refuses on a cold option instead of counting it (D7)
            const filterNotes: string[] = [];
            for (const optEntry of entries(spec.layer)) {
                const optId = plCostLayerEntryUuid(optEntry);
                if (optId !== '') { optionGps.push(Math.max(0.0, readXrefTarget(env.ctx, optId).pl * 100)); continue; }
                let optRaw = isObj(optEntry) || Array.isArray(optEntry) ? (typeof (optEntry as Row)['0'] === 'string' ? (optEntry as Row)['0'] as string : '') : (typeof optEntry === 'string' ? optEntry : '');
                optRaw = optRaw.trim();
                if (optRaw === '') continue;
                const lm = /(?:^|\|)[a-z_]*level:(\d+)/.exec(optRaw);
                if (lm) { const filterLevel = Math.max(1, Number.parseInt(lm[1]!, 10)); optionGps.push(env.spells.getCostForLevel(filterLevel) * 0.60); filterNotes.push('level-' + filterLevel + ' catalog pick'); }
                else { optionGps.push(2500.0); filterNotes.push('open catalog pick (flat 2,500 gp)'); }
            }
            if (optionGps.length === 0 && unresolved === 0) continue;
            let baseGp: number;
            if (spec.choice) baseGp = optionGps.length === 0 ? 0.0 : Math.min(Math.max(...optionGps) * 1.25, optionGps.reduce((s, v) => s + v, 0));
            else baseGp = optionGps.reduce((s, v) => s + v, 0);
            const gp = phpIntRound(baseGp * spec.fraction * statusDurationMult);
            if (gp === 0) continue;
            const uniqueNotes = [...new Set(filterNotes)];
            misc['unlock_' + spec.layer] = {
                value: optionGps.length + unresolved, gp,
                note: spec.label + ': ' + (optionGps.length + unresolved) + ' option(s)' + (spec.choice ? ' (strongest × 1.25, capped at sum)' : ' (sum)') + ' × ' + fstr(spec.fraction) + (uniqueNotes.length ? ' — ' + uniqueNotes.join('; ') : ''),
            };
        }
        for (const ddefBase of ['damage_type_resistance', 'damage_type_immunity']) {
            for (let ci = 1; ci <= 4; ci++) {
                const ul = 'user_choice_' + ci + '_' + ddefBase;
                const optEntries = entries(ul);
                if (optEntries.length === 0) continue;
                const optionGps: number[] = [];
                for (const optEntry of optEntries) {
                    const optId = plCostLayerEntryUuid(optEntry);
                    if (optId === '') continue;
                    const v = cats.damageTypes.getVariableName(optId);
                    if (!v) continue;
                    const base = costs.getDamageModifierCost(ddefBase, v);
                    if (base > 0) optionGps.push(base);
                }
                if (optionGps.length === 0) continue;
                const gp = phpIntRound(Math.min(Math.max(...optionGps) * 1.25, optionGps.reduce((s, v) => s + v, 0)) * statusDurationMult);
                if (gp === 0) continue;
                dmgDefGrants.push({ field: ul, damage_type: 'player choice of ' + optionGps.length, gp, derivation: { base_gp: phpIntRound(Math.max(...optionGps)), choice_multiplier: 1.25, duration_multiplier: statusDurationMult, duration_label: statusDurationLabel } });
            }
        }
        const spGrant = phpJsonInt(read('skill_points') ?? 0);
        if (spGrant !== 0) {
            const gp = phpIntRound(spGrant * 1000 * statusDurationMult);
            if (gp !== 0) misc.skill_points = { value: spGrant, gp, note: 'Skill point grant (' + spGrant + ' SP × 1,000 gp — the tier-curve rate)' };
        }
    }

    // FINAL SUM with the SELF-DRAWBACK CAP
    const gpOf = (rows: unknown): number[] => (Array.isArray(rows) ? rows : Object.values(rows as Row)).map(r => phpJsonFloat((r as Row).gp ?? 0));
    const atoms: number[] = [
        phpJsonFloat(components.damage_roll), phpJsonFloat(components.force_movement), phpJsonFloat(components.skill_roll_modifiers), phpJsonFloat(components.roll_debuffs), phpJsonFloat(components.aoe),
        phpJsonFloat((components.granted_categories as Row | undefined)?.gp ?? 0),
        ...gpOf(statusApps), ...gpOf(misc), ...gpOf(components.ap_modifiers), ...gpOf(components.light_modifiers), ...gpOf(rollGrants), ...gpOf(dmgDefGrants),
    ];
    let positiveGp = 0.0, negativeGp = 0.0;
    for (const atom of atoms) { if (atom >= 0) positiveGp += atom; else negativeGp += atom; }
    const cappedNegative = Math.max(negativeGp, -0.5 * positiveGp);
    if (cappedNegative > negativeGp) {
        const correction = phpIntRound(cappedNegative - negativeGp);
        if (correction !== 0) misc.self_drawback_cap = { value: 1, gp: correction, note: 'Self-drawback cap: drawbacks (' + phpIntRound(negativeGp) + ' gp) offset at most 50% of positive content (' + phpIntRound(positiveGp) + ' gp)' };
    }
    const numericSum = positiveGp + cappedNegative;
    return { gp: phpIntRound(numericSum), components };
}

import { sizeGatedDefenseGp } from './cost';
const sizeGated = (env: PassEnv, layer: string, n: number): number => sizeGatedDefenseGp(layer, n, env.costs);
